import {
  ActualityHeatmapDataRow,
  AssignmentHeatmapDataRow,
} from '@warebee/shared/dataset-queries';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { useRecoilValue, useRecoilValueLoadable } from 'recoil';
import {
  HeatmapBucket,
  getHeatmapBucketByValue,
} from '../../common/heatmap.helper';
import useFormatter from '../../common/useFormatter';
import { ActualityMetricDescriptor } from '../../metrics/actuality/actualityMetric.types';
import { ActualityAssignmentMetricDescriptor } from '../../metrics/actualityAssignment/actualityAssignmentMetric.types';
import { useActualityEffectiveMetricDescriptors } from '../../metrics/useActualityEffectiveMetricDescriptors';
import {
  actualityEffectiveHeatmapBuckets,
  actualityEffectiveHeatmapLevelData,
  actualityEffectiveHeatmapRangeDescriptor,
  actualityEffectiveMetric,
  actualityHeatmapBuckets,
  actualityHeatmapLevelData,
  actualityHeatmapRangeDescriptor,
  actualityMetricDescriptor,
} from '../store/actuality.heatmap.state';
import {
  assignmentHeatmapBuckets,
  assignmentHeatmapLevelData,
  assignmentHeatmapRangeDescriptor,
  assignmentMetricDescriptor,
} from '../store/assignment.heatmap.state';
import { feedItemsWithRankMap } from '../store/feed.state';
import { FeedItemWithABCRank } from '../store/feed.types';

export type ActualityHeatmapItem = ActualityHeatmapDataRow & {
  itemRanked: FeedItemWithABCRank;
  heatmapValue: string;
  bucket: HeatmapBucket;
  metricDescriptor:
    | ActualityMetricDescriptor
    | ActualityAssignmentMetricDescriptor;
  dataType: 'events' | 'assignment';
};

export type LocationItemsResult = {
  eventsItems: ActualityHeatmapItem[];
  assignmentItems: ActualityHeatmapItem[];
  allItems: ActualityHeatmapItem[];
};

/**
 *  Helpers for get actuality items with resolved heatmap value and bucket
 */
function useActualityHeatmapItems() {
  const { t } = useTranslation('feed');
  const formatter = useFormatter();
  const itemsWithRank = useRecoilValue(feedItemsWithRankMap);

  // Current effective data (based on selected type)
  const metric = useRecoilValue(actualityEffectiveMetric);
  const buckets = useRecoilValue(actualityEffectiveHeatmapBuckets);
  const heatmapData = useRecoilValue(actualityEffectiveHeatmapLevelData);
  const rangeDescriptor = useRecoilValue(
    actualityEffectiveHeatmapRangeDescriptor,
  );
  const metricDescriptorMap = useActualityEffectiveMetricDescriptors();

  // Events data
  const eventsMetricDescriptor = useRecoilValue(actualityMetricDescriptor);
  const eventsDataLoadable = useRecoilValueLoadable(actualityHeatmapLevelData);
  const eventsBucketsLoadable = useRecoilValueLoadable(actualityHeatmapBuckets);
  const eventsRangeDescriptorLoadable = useRecoilValueLoadable(
    actualityHeatmapRangeDescriptor,
  );

  // Assignment data
  const assignmentMetricDescriptorValue = useRecoilValue(
    assignmentMetricDescriptor,
  );
  const assignmentDataLoadable = useRecoilValueLoadable(
    assignmentHeatmapLevelData,
  );
  const assignmentBucketsLoadable = useRecoilValueLoadable(
    assignmentHeatmapBuckets,
  );
  const assignmentRangeDescriptorLoadable = useRecoilValueLoadable(
    assignmentHeatmapRangeDescriptor,
  );

  const bucketsMap = _.keyBy(buckets, b => b.id);
  const metricDescriptor = metricDescriptorMap[metric];

  function getHeatmapItem(
    dataRow: ActualityHeatmapDataRow | AssignmentHeatmapDataRow,
    metricDesc: any,
    bucketsDict: Record<string, HeatmapBucket>,
    rangeDesc: any,
    dataType: 'events' | 'assignment',
  ): ActualityHeatmapItem {
    const bucket = getHeatmapBucketByValue({
      data: {
        [metricDesc.path]: dataRow.dimensionValue,
      },
      buckets: bucketsDict,
      metricDescriptor: metricDesc as any,
      rangeDescriptor: rangeDesc,
    });

    const value = dataRow.dimensionValue?.toString();
    const heatmapValue = metricDesc.format
      ? metricDesc.format(parseFloat(value))
      : value;

    return {
      ...dataRow,
      heatmapValue,
      bucket,
      metricDescriptor: metricDesc,
      itemRanked: itemsWithRank[dataRow.skuKey],
      dataType,
    } as ActualityHeatmapItem;
  }

  function getLocationItems(locationId: string): ActualityHeatmapItem[] {
    if (_.isNil(locationId)) return [];

    const locationData = _.filter(
      heatmapData,
      row => row.locationId === locationId,
    );
    return _.map(locationData, row =>
      getHeatmapItem(
        row,
        metricDescriptor,
        bucketsMap,
        rangeDescriptor,
        'events',
      ),
    );
  }

  function getAllLocationItems(locationId: string): LocationItemsResult {
    if (_.isNil(locationId)) {
      return {
        eventsItems: [],
        assignmentItems: [],
        allItems: [],
      };
    }

    let eventsItems: ActualityHeatmapItem[] = [];
    let assignmentItems: ActualityHeatmapItem[] = [];

    // Get events data
    if (
      eventsDataLoadable.state === 'hasValue' &&
      eventsBucketsLoadable.state === 'hasValue' &&
      eventsRangeDescriptorLoadable.state === 'hasValue'
    ) {
      const eventsData = eventsDataLoadable.contents;
      const eventsBuckets = _.keyBy(eventsBucketsLoadable.contents, b => b.id);
      const eventsRangeDesc = eventsRangeDescriptorLoadable.contents;

      const locationEventsData = _.filter(
        eventsData,
        row => row.locationId === locationId,
      );

      eventsItems = _.map(locationEventsData, row =>
        getHeatmapItem(
          row,
          eventsMetricDescriptor,
          eventsBuckets,
          eventsRangeDesc,
          'events',
        ),
      );
    }

    // Get assignment data
    if (
      assignmentDataLoadable.state === 'hasValue' &&
      assignmentBucketsLoadable.state === 'hasValue' &&
      assignmentRangeDescriptorLoadable.state === 'hasValue'
    ) {
      const assignmentData = assignmentDataLoadable.contents;
      const assignmentBuckets = _.keyBy(
        assignmentBucketsLoadable.contents,
        b => b.id,
      );
      const assignmentRangeDesc = assignmentRangeDescriptorLoadable.contents;

      const locationAssignmentData = _.filter(
        assignmentData,
        row => row.locationId === locationId,
      );

      assignmentItems = _.map(locationAssignmentData, row =>
        getHeatmapItem(
          row,
          assignmentMetricDescriptorValue,
          assignmentBuckets,
          assignmentRangeDesc,
          'assignment',
        ),
      );
    }

    const allItems = [...eventsItems, ...assignmentItems];

    return {
      eventsItems,
      assignmentItems,
      allItems,
    };
  }

  return {
    getLocationItems,
    getAllLocationItems,
  };
}

export default useActualityHeatmapItems;
